/*
 *   Copyright (c) 2021 vlinder Labs Private Limited
 *   All rights reserved.
 *   This software is proprietary of Vlinder Labs Pvt. Ltd and can not be distributed / used for any purpose outside of Vlinder Labs Pvt. Ltd without explicit written consent from the copyright owner.
 */

import {
  IAgentPlugin,
  IPluginMethodMap,
} from "@vlinder-web/plugin-agent-service-react";
import axios from "axios";
import _ from "lodash";
import { toHeaderCase } from "js-convert-case";
import moment from "moment";
import { LoggerModule } from "@vlinder-web/logger-module-react";

import config from "../config/rowDetail.config.json";

import { useRowId } from "../provider";

import { GraphqlServerModule } from "@vlinder-web/graphql-server-module-react";
import { GET_ROW_DETAIL } from "../graphql";
import { VApplication } from "../../../application";
import { AppNavigatorService } from "../../appNavigatorService";
import { capitalCase } from "change-case";

const log: any = LoggerModule?.getInstance();
const getGraphQLModule = () => {
  const app = VApplication.getInstance();
  return app.getSync("services.GraphqlServerModule") as GraphqlServerModule;
};

const getWhoamiSrv = () => {
  const app = VApplication.getInstance();
  return app.getSync("services.AppNavigatorService") as AppNavigatorService;
};

export interface IRowDetailMethods extends IPluginMethodMap {
  getRowDetail(args: any): Promise<any>;
}

function isValidHttpUrl(string: string) {
  let url;

  try {
    url = new URL(string);
  } catch (_) {
    return false;
  }

  return url.protocol === "http:" || url.protocol === "https:";
}

export class RowDetailService implements IAgentPlugin {
  readonly methods: IRowDetailMethods;

  constructor() {
    this.methods = {
      getRowDetail: this.getRowDetail.bind(this),
    };
  }

  async getRowDetail(args: any) {
    const graphqlModule = getGraphQLModule();
    const client = graphqlModule.getClient("graphql-gateway");
    const { rowId } = useRowId();
    try {
      if (client) {
        const res = await client.query({
          query: GET_ROW_DETAIL,
          variables: {
            filters: {
              ...args?.criteria,
              _id__in: rowId,
            },
            options: {
              skip: args?.offset ?? 0,
              limit: args?.size ?? 10,
            },
          },
          fetchPolicy: "network-only",
        });
        if (res?.data?.getUsersWithBoxes) {
          log.success(
            `get box data in boxDetail screen :`,
            res?.data?.getUsersWithBoxes
          );
          return {
            isEmpty: res?.data?.getUsersWithBoxes?.content?.length <= 0,
            title: this.formatTitle(res?.data?.getUsersWithBoxes?.content[0]),
            subtitle: this.formatSubTitle(
              res?.data?.getUsersWithBoxes?.content[0]
            ),
            listItems: this.formatAttributeItems(
              res?.data?.getUsersWithBoxes?.content[0]
            ),
            //chips: this.formatChips(res?.data?.getUsersWithBoxes?.content[0]),
          };
        }
      }
    } catch (err: any) {
      return err;
    }
  }

  private formatChips(row: any) {
    let _result: any[] = [];
    const _role: any = row?.role ? row?.role : [];
    _role &&
      Array.isArray(_role) &&
      _role.length > 0 &&
      _role.map((role: any) => {
        _result.push({
          ...role,
          key: role?.uuid,
        });
      });

    return _result;
  }

  private formatTitle(row: any) {
    let formatted = row?.fullName ? row?.fullName : "";
    return toHeaderCase(formatted);
  }

  private formatSubTitle(row: any) {
    let formatted = row?.email ? row?.email : "";
    return formatted;
  }

  private formatAttributeItems(user: any) {
    console.log(" users in formatAttributeItems : ", user);
    let formatted: any = {};
    formatted = {
      info: config?.attributeItems,
    };
    const row = user?._box;
    //if (row) {
    const masqIDindex = _.findIndex(
      formatted.info,
      (info: any) => info.key === "masqID"
    );
    if (masqIDindex > -1) {
      formatted.info[masqIDindex].subTitle = row?.masqID.toString() ?? null;
    }
    const costumeIDIndex = _.findIndex(
      formatted.info,
      (info: any) => info.key === "costumeID"
    );
    if (costumeIDIndex > -1) {
      formatted.info[costumeIDIndex].subTitle = row?.costumeID ?? null;
    }
    const isVIPindex = _.findIndex(
      formatted.info,
      (info: any) => info.key === "isVIP"
    );
    if (isVIPindex > -1) {
      formatted.info[isVIPindex].subTitle =
        row?.isVIP && row?.isVIP.toString()
          ? row?.isVIP === true
            ? "Yes"
            : "No"
          : null;
    }
    const genderindex = _.findIndex(
      formatted.info,
      (info: any) => info.key === "gender"
    );
    if (genderindex > -1) {
      formatted.info[genderindex].subTitle = row?.gender.toString() ?? null;
    }
    const contentStatusindex = _.findIndex(
      formatted.info,
      (info: any) => info.key === "contentStatus"
    );
    if (contentStatusindex > -1) {
      formatted.info[contentStatusindex].subTitle =
        (row?.contentStatus && capitalCase(row?.contentStatus.toString())) ??
        null;
    }
    const paymentStatusindex = _.findIndex(
      formatted.info,
      (info: any) => info.key === "paymentStatus"
    );
    if (paymentStatusindex > -1) {
      formatted.info[paymentStatusindex].subTitle =
        (row?.paymentStatus && capitalCase(row?.paymentStatus.toString())) ??
        null;
    }
    const deliveryStatusindex = _.findIndex(
      formatted.info,
      (info: any) => info.key === "deliveryStatus"
    );
    if (deliveryStatusindex > -1) {
      formatted.info[deliveryStatusindex].subTitle =
        (row?.deliveryStatus && capitalCase(row?.deliveryStatus.toString())) ??
        null;
    }
    const primaryContactNoindex = _.findIndex(
      formatted.info,
      (info: any) => info.key === "primaryContactNo"
    );
    console.log("primaryContactNoindex : ", primaryContactNoindex, user);
    if (primaryContactNoindex > -1) {
      formatted.info[primaryContactNoindex].subTitle = this.formtPhoneNumber(
        user?.primaryContactNo,
        user?.phoneNumbers
      );
    }
    // }
    return formatted?.info;
  }

  private formtPhoneNumber = (phone: string, phones: any[]) => {
    console.log("phone : ", phone, " phones : ", phones);
    if (phone) {
      return phone;
    } else if (phones && Array.isArray(phones) && phones.length > 0) {
      return phones[0]?.number;
    } else {
      return null;
    }
  };
}
